import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  MoreHorizontal,
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
  ExternalLink,
  Calendar,
  Image,
  Video,
  Link as LinkIcon,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent } from '../../components/Card';
import { Badge, getStatusVariant } from '../../components/Badge';
import { Button } from '../../components/Button';
import { socialPostsApi } from '../../lib/api';
import { format } from 'date-fns';
import type { SocialPost, SocialPlatform, SocialPostStatus } from '../../types';

const PLATFORM_OPTIONS: SocialPlatform[] = ['linkedin', 'facebook', 'instagram', 'twitter'];
const STATUS_OPTIONS: SocialPostStatus[] = ['draft', 'scheduled', 'publishing', 'published', 'failed'];

function getPlatformIcon(platform: SocialPlatform) {
  switch (platform) {
    case 'linkedin':
      return <Linkedin className="w-5 h-5" />;
    case 'facebook':
      return <Facebook className="w-5 h-5" />;
    case 'instagram':
      return <Instagram className="w-5 h-5" />;
    case 'twitter':
      return <Twitter className="w-5 h-5" />;
    default:
      return null;
  }
}

function getPlatformColor(platform: SocialPlatform) {
  switch (platform) {
    case 'linkedin':
      return 'bg-blue-100 text-blue-600';
    case 'facebook':
      return 'bg-indigo-100 text-indigo-600';
    case 'instagram':
      return 'bg-pink-100 text-pink-600';
    case 'twitter':
      return 'bg-sky-100 text-sky-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

export function SocialPosts() {
  const queryClient = useQueryClient();
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['social-posts', platformFilter, statusFilter],
    queryFn: () =>
      socialPostsApi.list({
        platform: platformFilter || undefined,
        status: statusFilter || undefined,
        limit: 100,
      }),
  });

  const updatePostMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SocialPostStatus }) =>
      socialPostsApi.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] });
    },
  });

  const posts = (data?.posts || []) as SocialPost[];

  // Filter by search query
  const filteredPosts = posts.filter((post) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return post.post_text.toLowerCase().includes(query);
  });

  // Calculate stats
  const stats = {
    total: posts.length,
    published: posts.filter((p) => p.status === 'published').length,
    scheduled: posts.filter((p) => p.status === 'scheduled').length,
    failed: posts.filter((p) => p.status === 'failed').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Posts</h1>
          <p className="text-gray-500 mt-1">Manage your social media content</p>
        </div>
        <Button>
          <Plus className="w-4 h-4" />
          New Post
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Posts</p>
            <p className="text-2xl font-semibold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Published</p>
            <p className="text-2xl font-semibold text-green-600">{stats.published}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Scheduled</p>
            <p className="text-2xl font-semibold text-blue-600">{stats.scheduled}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Failed</p>
            <p className="text-2xl font-semibold text-red-600">{stats.failed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <select
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Platforms</option>
          {PLATFORM_OPTIONS.map((platform) => (
            <option key={platform} value={platform}>
              {platform.charAt(0).toUpperCase() + platform.slice(1)}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Posts List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No posts found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPosts.map((post) => (
            <Card key={post.id} className="hover:shadow-md transition-shadow">
              <div className="p-5">
                <div className="flex gap-4">
                  {/* Platform Icon */}
                  <div
                    className={`flex-shrink-0 p-3 rounded-lg ${getPlatformColor(post.platform)}`}
                  >
                    {getPlatformIcon(post.platform)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-gray-900 whitespace-pre-wrap line-clamp-3">
                          {post.post_text}
                        </p>

                        {/* Media & Link indicators */}
                        <div className="flex items-center gap-3 mt-2">
                          {post.media_url && (
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              {post.media_type === 'video' ? (
                                <Video className="w-4 h-4" />
                              ) : (
                                <Image className="w-4 h-4" />
                              )}
                              <span>{post.media_type || 'media'}</span>
                            </div>
                          )}
                          {post.link_url && (
                            <div className="flex items-center gap-1 text-sm text-gray-500">
                              <LinkIcon className="w-4 h-4" />
                              <span className="truncate max-w-48">{post.link_url}</span>
                            </div>
                          )}
                        </div>

                        {/* Hashtags */}
                        {post.hashtags && (
                          <p className="text-sm text-blue-600 mt-2">{post.hashtags}</p>
                        )}

                        {/* Error message */}
                        {post.status === 'failed' && post.error_message && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            <span>{post.error_message}</span>
                          </div>
                        )}
                      </div>

                      <button className="p-1 hover:bg-gray-100 rounded">
                        <MoreHorizontal className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        <Badge variant={getStatusVariant(post.status)}>{post.status}</Badge>

                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {post.published_at
                              ? `Published ${format(new Date(post.published_at), 'MMM d, h:mm a')}`
                              : post.scheduled_for
                              ? `Scheduled ${format(new Date(post.scheduled_for), 'MMM d, h:mm a')}`
                              : `Created ${format(new Date(post.created_at), 'MMM d')}`}
                          </span>
                        </div>

                        {post.platform_url && (
                          <a
                            href={post.platform_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                            <span>View</span>
                          </a>
                        )}
                      </div>

                      {/* Quick status change */}
                      <select
                        value={post.status}
                        onChange={(e) =>
                          updatePostMutation.mutate({
                            id: post.id,
                            status: e.target.value as SocialPostStatus,
                          })
                        }
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
